import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPinIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  CalendarIcon,
  MapIcon,
  UserIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';

// --- Utility Functions ---
const getFormattedDateTime = () => {
  const now = new Date();
  return {
    time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }),
    date: now.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }),
    day: now.toLocaleDateString([], { weekday: 'long' })
  };
};

const mockSites = [
  'Sector 45 Business Hub, Gurgaon',
  'Prestige Tech Park, Bangalore',
  'Industrial Estate, Phase II, Pune',
  'TCS Synergy Park, Hyderabad',
];

const ContinuousLocationPunching = () => {
  // Assuming these details come from the logged-in user context
  const currentUser = {
    name: "Rahul Sharma",
    empId: "EMP-2025-084",
    designation: "Senior Field Engineer"
  };

  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [liveTime, setLiveTime] = useState(getFormattedDateTime());

  // Update live clock
  useEffect(() => {
    const timer = setInterval(() => setLiveTime(getFormattedDateTime()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCheckIn = () => {
    const { time, date } = getFormattedDateTime();
    const currentSite = mockSites[Math.floor(Math.random() * mockSites.length)];
    
    const newEntry = {
      id: Date.now(),
      date: date,
      checkInTime: time,
      checkInLoc: currentSite,
      checkOutTime: '--:--',
      checkOutLoc: '--:--',
      status: 'In-Progress'
    };

    setCurrentSessionId(newEntry.id);
    setAttendanceLogs([newEntry, ...attendanceLogs]);
    setIsPunchedIn(true);
  };

  const handleCheckOut = () => {
    const { time } = getFormattedDateTime();
    const checkoutSite = attendanceLogs.find(l => l.id === currentSessionId).checkInLoc;

    setAttendanceLogs(prev => prev.map(log => 
      log.id === currentSessionId 
        ? { ...log, checkOutTime: time, checkOutLoc: checkoutSite, status: 'Completed' }
        : log
    ));

    setIsPunchedIn(false);
    setCurrentSessionId(null);
  };

  return (
    <div className="min-h-screen bg-white p-4 md:p-8" style={{ fontFamily: "'Roboto', sans-serif" }}>
      
      {/* 1. TOP ERP HEADER */}
      <div className="max-w-7xl mx-auto border-b-2 border-gray-100 pb-6 mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Field Location Punching
          </h1>
          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1 font-medium"><UserIcon className="h-4 w-4" /> {currentUser.name}</span>
            <span className="flex items-center gap-1 font-medium"><IdentificationIcon className="h-4 w-4" /> {currentUser.empId}</span>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right border-r pr-6 border-gray-200">
            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Current Session</p>
            <p className="text-lg font-black text-slate-800 tabular-nums">{liveTime.time}</p>
          </div>
          
          {!isPunchedIn ? (
            <button
              onClick={handleCheckIn}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded shadow-sm text-sm font-bold flex items-center gap-2 transition-all active:scale-95"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              START CHECK-IN
            </button>
          ) : (
            <button
              onClick={handleCheckOut}
              className="bg-rose-600 hover:bg-rose-700 text-white px-6 py-2.5 rounded shadow-sm text-sm font-bold flex items-center gap-2 transition-all active:scale-95"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              STOP CHECK-OUT
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        
        {/* 2. STATS OVERVIEW CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-50 p-5 rounded border border-gray-200">
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">Current Site</p>
            <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <MapIcon className="h-4 w-4 text-indigo-500" />
              {isPunchedIn ? attendanceLogs[0].checkInLoc : "Not at any site"}
            </p>
          </div>
          <div className="bg-gray-50 p-5 rounded border border-gray-200">
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">Punch Status</p>
            <div className="flex items-center gap-2">
              <div className={`h-2.5 w-2.5 rounded-full ${isPunchedIn ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
              <p className="text-sm font-bold text-gray-800">{isPunchedIn ? 'Currently Working' : 'Logged Out'}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-5 rounded border border-gray-200">
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">Today's Date</p>
            <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              {liveTime.date} ({liveTime.day})
            </p>
          </div>
        </div>

        {/* 3. CLEAN TABULAR LOGS */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Location & Activity Logs
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white border-b border-gray-200">
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase">Date</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase">In-Punch</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase">Out-Punch</th>
                  <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase">Site Location</th>
                  <th className="px-6 py-4 text-center text-[11px] font-bold text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {attendanceLogs.map((log) => (
                    <motion.tr
                      key={log.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-5 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{log.date}</span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-bold text-gray-700">{log.checkInTime}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4 text-rose-400" />
                          <span className="text-sm font-bold text-gray-700">{log.checkOutTime}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-start gap-1">
                          <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm text-gray-700 leading-tight">{log.checkInLoc}</p>
                            {log.checkOutLoc !== '--:--' && (
                              <p className="text-[10px] text-gray-400 mt-1 italic">Verified at Checkout</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <span className={`px-3 py-1 rounded text-[10px] font-bold uppercase ${
                          log.status === 'In-Progress' 
                            ? 'bg-blue-50 text-blue-600 border border-blue-100' 
                            : 'bg-green-50 text-green-600 border border-green-100'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            
            {attendanceLogs.length === 0 && (
              <div className="py-24 text-center">
                <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapIcon className="h-8 w-8 text-gray-300" />
                </div>
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest">No Logs Found</h4>
                <p className="text-xs text-gray-400 mt-1">Please check-in to record your first site location.</p>
              </div>
            )}
          </div>
        </div>

        {/* 4. FOOTER INFO */}
        <div className="mt-8 flex justify-between items-center text-[11px] text-gray-400 font-medium uppercase tracking-widest">
          <p>Location ERP v2.0</p>
          <p>© 2025 Enterprise Solutions</p>
        </div>
      </div>
    </div>
  );
};

export default ContinuousLocationPunching;